import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Upload, Loader2, Camera } from "lucide-react";
import { toast } from "sonner";

export default function ReceiptUpload({ onExtracted }) {
  const inputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result;
      setPreview(base64);
      setLoading(true);
      try {
        const res = await api.post("/receipts/extract", { image_base64: base64 });
        onExtracted({ ...res.data, image: base64 });
        toast.success("Receipt scanned! Review the details below.");
      } catch (err) {
        if (err.response?.status === 402) {
          toast.error("Free limit reached", { description: "Upgrade to keep scanning receipts." });
        } else {
          toast.error("Could not read receipt", { description: err.response?.data?.detail || "Try a clearer photo." });
        }
        setPreview(null);
      } finally {
        setLoading(false);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="w-full">
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        capture="environment"
        className="hidden"
        data-testid="receipt-file-input"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <div
        data-testid="receipt-dropzone"
        onClick={() => !loading && inputRef.current?.click()}
        className={`group cursor-pointer rounded-2xl border-2 border-dashed border-border hover:border-primary bg-secondary/40 hover:bg-primary/5 transition-colors duration-200 p-12 flex flex-col items-center justify-center text-center ${loading ? "pointer-events-none opacity-90" : ""}`}
      >
        {loading ? (
          <>
            {preview && <img src={preview} alt="receipt" className="h-24 rounded-lg mb-4 object-contain shadow-sm" />}
            <Loader2 className="h-8 w-8 text-primary animate-spin mb-3" />
            <p className="font-heading font-semibold text-lg">Reading your receipt…</p>
            <p className="text-sm text-muted-foreground">AI is extracting vendor, amount & category</p>
          </>
        ) : (
          <>
            <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-200">
              <Camera className="h-7 w-7 text-primary" />
            </div>
            <p className="font-heading font-bold text-lg">Snap or upload a receipt</p>
            <p className="text-sm text-muted-foreground mt-1 mb-5">PNG, JPG or WEBP · AI extracts everything</p>
            <Button data-testid="upload-receipt-button" className="rounded-xl bg-primary hover:bg-primary/90 transition-colors duration-200">
              <Upload className="h-4 w-4 mr-2" /> Choose photo
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
